import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="post-deletion"
export default class extends Controller {
  static targets = ["deleteButton"]
  static values = { url: String }
  
  connect() {
    // Add data attributes for animations
    this.element.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
  }
  
  async delete(event) {
    event.preventDefault()
    event.stopPropagation()
    
    // Show confirmation
    if (!confirm("Are you sure you want to delete this post? This action cannot be undone.")) {
      return
    }
    
    // Start deletion animation
    await this.animateDelete()
    
    // Perform deletion
    try {
      const response = await fetch(this.urlValue, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin'
      })
      
      if (response.ok) {
        // Success! Post is already animated out, just remove from DOM
        this.element.remove()
        
        // Show success toast
        this.showSuccessToast()
      } else {
        // Error - reverse the animation
        this.reverseAnimation()
        this.showErrorToast()
      }
    } catch (error) {
      // Network error - reverse the animation
      this.reverseAnimation()
      this.showErrorToast()
    }
  }
  
  async animateDelete() {
    const post = this.element
    
    // First phase: Fade out and scale down slightly
    post.style.transform = 'scale(0.98)'
    post.style.opacity = '0.6'
    
    await this.wait(100)
    
    // Second phase: Slide up and fade out more
    post.style.transform = 'scale(0.95) translateY(-10px)'
    post.style.opacity = '0.3'
    
    await this.wait(150)
    
    // Final phase: Collapse height and fade out completely
    const originalHeight = post.offsetHeight
    post.style.height = `${originalHeight}px`
    post.style.overflow = 'hidden'
    
    await this.wait(50)
    
    post.style.height = '0px'
    post.style.opacity = '0'
    post.style.transform = 'scale(0.9) translateY(-20px)'
    post.style.marginBottom = '0px'
    post.style.paddingTop = '0px'
    post.style.paddingBottom = '0px'
    
    await this.wait(300)
  }
  
  reverseAnimation() {
    const post = this.element
    
    // Reset all styles to bring the post back
    post.style.transform = ''
    post.style.opacity = ''
    post.style.height = ''
    post.style.overflow = ''
    post.style.marginBottom = ''
    post.style.paddingTop = ''
    post.style.paddingBottom = ''
    
    // Add a subtle bounce back effect
    post.style.animation = 'bounce 0.5s ease-out'
    
    setTimeout(() => {
      post.style.animation = ''
    }, 500)
  }
  
  showSuccessToast() {
    this.showToast('Post deleted successfully', 'success')
  }
  
  showErrorToast() {
    this.showToast('Failed to delete post. Please try again.', 'error')
  }
  
  showToast(message, type) {
    // Create toast element
    const toast = document.createElement('div')
    toast.className = `fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-[10000] transform translate-x-full transition-transform duration-300 ${
      type === 'success' 
        ? 'bg-green-500 text-white' 
        : 'bg-red-500 text-white'
    }`
    
    toast.innerHTML = `
      <div class="flex items-center">
        <i class="fas ${type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'} mr-2"></i>
        <span>${message}</span>
      </div>
    `
    
    document.body.appendChild(toast)
    
    // Slide in
    requestAnimationFrame(() => {
      toast.style.transform = 'translate-x-0'
    })
    
    // Auto remove after 3 seconds
    setTimeout(() => {
      toast.style.transform = 'translate-x-full'
      setTimeout(() => {
        document.body.removeChild(toast)
      }, 300)
    }, 3000)
  }
  
  wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}

// Add CSS for bounce animation
const style = document.createElement('style')
style.textContent = `
  @keyframes bounce {
    0%, 20%, 53%, 80%, 100% {
      transform: translate3d(0,0,0);
    }
    40%, 43% {
      transform: translate3d(0,-5px,0);
    }
    70% {
      transform: translate3d(0,-3px,0);
    }
    90% {
      transform: translate3d(0,-1px,0);
    }
  }
`
document.head.appendChild(style)